import { useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, type MessageDescriptor } from "react-intl";

import Button, { ButtonGroup } from "@atlaskit/button";
import StatusIcon from "@atlaskit/icon/core/status-information";
import VideoPlayOverlayIcon from "@atlaskit/icon/core/video-play-overlay";
import RandomizeIcon from "@atlaskit/icon-lab/core/randomize";

import { wacToken } from "@/libraries/design/foundations";
import { messages } from "@/templates/paid-landing/sections/cfind-4666-frictionless-first-look/messages.ts";
import { useJiraAiFFL } from "@/templates/paid-landing/src/hooks/use-jira-ai-ffl-context";

import { TeamType, teamTypesMap } from "../../utils/paid-jira-ai-ffl.ts";

import { PaidJiraAiMadLib } from "./paid-jira-ai-mad-lib.tsx";

const modalCss = css({
    display: "inline-block",
    width: "100%",
    maxWidth: "100%",
    boxShadow:
        // eslint-disable-next-line local/ensure-wac-token-usage
        "0px 0px 1px rgba(30, 31, 33, 0.31), 0px 8px 12px rgba(30, 31, 33, 0.15)",
    borderRadius: "16px",
    background: wacToken("color.neutral.light0"),
    padding: "24px 20px",
    textAlign: "left"
});

const teamTypeContainerCss = css({
    marginBlockEnd: "16px"
});

const teamTypeButtonsGroupCss = css({
    marginInlineStart: -4,
    marginBlockEnd: -4
});

const teamTypeButtonStyles = {
    fontWeight: 400,
    borderRadius: 8,
    marginInlineStart: 4,
    marginBlockEnd: 4
};

const footerCss = css({
    marginBlockStart: "16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
});

const disclaimerCss = css({
    fontFamily: "var(--atlassian-sans-font-family)",
    fontSize: "12px",
    lineHeight: "16px",
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#6B6E76"
});

const poweredByAiCss = css({
    display: "flex",
    alignItems: "center",
    gap: "4px"
});

const shuffleButtonStyles = {
    borderRadius: "40px",
    paddingLeft: "4px",
    paddingRight: "4px",
    fontSize: "16px",
    lineHeight: "20px",
    fontWeight: 400,
    alignItems: "center"
};

const actionButtonStyles = {
    borderRadius: "40px",
    paddingLeft: "24px",
    paddingRight: "24px",
    fontSize: "16px",
    lineHeight: "20px",
    alignItems: "center"
};

export interface MadlibSubmitParams {
    teamType: TeamType;
    workingOnValue: string;
    goalValue: string;
}

export interface PaidJiraAiTeamTypeMadLibPromptProp {
    isRerun?: boolean;
    showLoadingState?: boolean;
    ctaMessage?: MessageDescriptor;
    ctaKey?: string;
}

export const PaidJiraAiTeamTypeMadLibPrompt = ({
    isRerun,
    showLoadingState,
    ctaMessage,
    ctaKey
}: PaidJiraAiTeamTypeMadLibPromptProp) => {
    const {
        submit,
        cgsDataLoading,
        selectedTeamType,
        setSelectedTeamType,
        workingOnSuggestion,
        goalSuggestion,
        workingOnValue,
        goalValue,
        setWorkingOnValue,
        setGoalValue,
        setHasUserSelectedTeamType,
        hasUserSelectedTeamType,
        shuffle,
        suggestMadLib,
        setIsErrorFromCgs,
        firePaidJiraAi,
        markPromptChangedField,
        commonAnalyticsAttributes,
        promptAttemptIndex
    } = useJiraAiFFL();

    const [isFormValid, setIsFormValid] = useState(true);
    const submitCtaMessage = ctaMessage ?? messages.getStarted;

    const handleOnSubmit = async () => {
        if (!isFormValid) {
            return;
        }

        // Track click intent separately from the async lifecycle events in the context.
        firePaidJiraAi("BuildWithAIClicked", "clicked", {
            cta: isRerun ? "rerunPrompt" : ctaKey ?? "getStarted",
            promptAttemptIndex: promptAttemptIndex + 1
        });

        const params: MadlibSubmitParams = {
            teamType: selectedTeamType,
            workingOnValue: workingOnValue || workingOnSuggestion,
            goalValue: goalValue || goalSuggestion
        };

        await submit({
            teamType: params.teamType,
            workingOn: params.workingOnValue,
            goal: params.goalValue
        });
    };

    return (
        <div css={modalCss}>
            {!isRerun ? (
                <div css={teamTypeContainerCss}>
                    <div css={teamTypeButtonsGroupCss}>
                        {Object.entries(teamTypesMap).map(
                            ([teamType, { messageKey }]) => (
                                <Button
                                    key={teamType}
                                    appearance={
                                        selectedTeamType === teamType
                                            ? "subtle"
                                            : "default"
                                    }
                                    isSelected={selectedTeamType === teamType}
                                    style={teamTypeButtonStyles}
                                    onClick={() => {
                                        markPromptChangedField("teamType");
                                        firePaidJiraAi(
                                            "PromptTeamTypeSelected",
                                            "clicked",
                                            {
                                                previousTeamType:
                                                    selectedTeamType,
                                                newTeamType: teamType,
                                                isFirstSelection:
                                                    !hasUserSelectedTeamType
                                            }
                                        );
                                        setHasUserSelectedTeamType(true);
                                        setSelectedTeamType(
                                            teamType as TeamType
                                        );
                                        suggestMadLib(teamType as TeamType);
                                    }}
                                >
                                    <FormattedMessage
                                        {...messages[messageKey]}
                                    />
                                </Button>
                            )
                        )}
                    </div>
                </div>
            ) : null}
            <PaidJiraAiMadLib
                focusInput={!isRerun}
                workingOnSuggestion={workingOnSuggestion}
                goalSuggestion={goalSuggestion}
                workingOnValue={workingOnValue}
                goalValue={goalValue}
                onEnter={handleOnSubmit}
                onChange={({
                    workingOnValue: newWorkingOnValue,
                    goalValue: newGoalValue
                }) => {
                    if (newWorkingOnValue !== workingOnValue) {
                        setIsErrorFromCgs(false);
                        setWorkingOnValue(newWorkingOnValue);
                        if (
                            !commonAnalyticsAttributes.changedFields?.includes(
                                "workingOn"
                            )
                        ) {
                            firePaidJiraAi("PromptFieldChanged", "clicked", {
                                field: "workingOn",
                                valueLength: newWorkingOnValue.trim().length,
                                isCustom: Boolean(newWorkingOnValue.trim())
                            });
                        }
                        markPromptChangedField("workingOn");
                    }
                    if (newGoalValue !== goalValue) {
                        setIsErrorFromCgs(false);
                        setGoalValue(newGoalValue);
                        if (
                            !commonAnalyticsAttributes.changedFields?.includes(
                                "goal"
                            )
                        ) {
                            firePaidJiraAi("PromptFieldChanged", "clicked", {
                                field: "goal",
                                valueLength: newGoalValue.trim().length,
                                isCustom: Boolean(newGoalValue.trim())
                            });
                        }
                        markPromptChangedField("goal");
                    }
                }}
                setAreInputsValid={setIsFormValid}
            />
            <div css={footerCss}>
                <div css={disclaimerCss}>
                    {!isRerun ? (
                        <div css={poweredByAiCss}>
                            <FormattedMessage {...messages.poweredByAi} />
                            <StatusIcon label="Status info" />
                        </div>
                    ) : null}
                </div>
                <ButtonGroup>
                    {cgsDataLoading || showLoadingState ? (
                        <Button
                            isDisabled
                            appearance="subtle"
                            style={actionButtonStyles}
                        >
                            <FormattedMessage {...messages.working} />
                        </Button>
                    ) : (
                        <>
                            <Button
                                appearance="subtle"
                                iconBefore={<RandomizeIcon label="" />}
                                style={shuffleButtonStyles}
                                onClick={shuffle}
                            >
                                <FormattedMessage {...messages.shuffle} />
                            </Button>
                            {isRerun ? (
                                <Button
                                    isDisabled={!isFormValid}
                                    appearance="primary"
                                    iconBefore={
                                        <VideoPlayOverlayIcon label="" />
                                    }
                                    style={actionButtonStyles}
                                    onClick={handleOnSubmit}
                                >
                                    <FormattedMessage
                                        {...messages.rerunPrompt}
                                    />
                                </Button>
                            ) : (
                                <Button
                                    isDisabled={!isFormValid}
                                    appearance="primary"
                                    style={actionButtonStyles}
                                    onClick={handleOnSubmit}
                                >
                                    <FormattedMessage {...submitCtaMessage} />
                                </Button>
                            )}
                        </>
                    )}
                </ButtonGroup>
            </div>
        </div>
    );
};
